import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Empty, Input, Modal, Select, goldToast } from "gold-ui-kit";
import FitDataGrid from "../components/classic/FitDataGrid";
import AppShell from "../components/classic/AppShell";
import ClassicWindow from "../components/classic/ClassicWindow";
import Toolbar, { ToolbarButton, ToolbarSeparator } from "../components/classic/Toolbar";
import { fetchEmployees } from "../store/slices/employees";
import { fetchJobsApi, updateBulkEmployeeJobsApi } from "../store/slices/employees/employeesApi";
import { useAuth } from "../context/AuthContext";
import { Calendar, RotateCw, FileText, UserCheck, UserX, Briefcase, Save, Search } from "lucide-react";

const YEAR_OPTIONS = [2023, 2024, 2025, 2026, 2027].map((y) => ({ value: y, label: String(y) }));
const MONTH_OPTIONS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"].map(
  (name, i) => ({ value: i + 1, label: `${name} (${String(i + 1).padStart(2, "0")})` })
);

const isExitedEmployee = (e) => !!e.exitDate || !!e.ExitDate || !!e.resignationDate || !!e.ResignationDate;

const resolveOriginalJobId = (e, jobs) => {
  const empJobId = e.jobId ?? e.job_id ?? e.jobID ?? e.job?.id ?? "";
  if (empJobId) {
    const matched = jobs.find((j) => String(j.id).trim().toLowerCase() === String(empJobId).trim().toLowerCase());
    return matched ? matched.id : String(empJobId);
  }
  if (e.jobTitle || typeof e.job === "string") {
    const title = e.jobTitle || e.job;
    const matched = jobs.find((j) => String(j.title || "").trim().toLowerCase() === String(title || "").trim().toLowerCase());
    return matched ? matched.id : "";
  }
  return "";
};

const PersonelListePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { list: employees, loading, error } = useSelector((s) => s.employees);
  const [search, setSearch] = useState("");

  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsSaving, setJobsSaving] = useState(false);
  const [jobSelections, setJobSelections] = useState({});
  const [modalSearch, setModalSearch] = useState("");

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  const loadEmployees = useCallback(() => {
    if (user?.branchId) dispatch(fetchEmployees({ branchId: user.branchId, year, month }));
  }, [dispatch, user?.branchId, year, month]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleOpenJobModal = async () => {
    setJobModalOpen(true);
    setJobsLoading(true);
    try {
      const loadedJobs = (await fetchJobsApi()) || [];
      setJobs(loadedJobs);
      const initialSelections = {};
      (employees || []).forEach((e) => {
        initialSelections[e.id] = resolveOriginalJobId(e, loadedJobs);
      });
      setJobSelections(initialSelections);
    } catch (err) {
      console.error("Görev listesi alınamadı:", err);
      goldToast.error("Görev listesi alınırken bir hata oluştu.");
    } finally {
      setJobsLoading(false);
    }
  };

  const handleSaveJobs = async () => {
    const updates = [];
    (employees || []).forEach((e) => {
      const selectedJobId = jobSelections[e.id];
      const originalJobId = e.jobId || e.job?.id || "";
      if (selectedJobId && selectedJobId !== originalJobId) {
        updates.push({ jobId: selectedJobId, employeeId: e.id });
      }
    });

    if (updates.length === 0) {
      goldToast.info("Herhangi bir görev değişikliği yapılmadı.");
      return;
    }

    setJobsSaving(true);
    try {
      await updateBulkEmployeeJobsApi(updates);
      goldToast.success("Toplu görev güncellemeleri başarıyla kaydedildi.");
      loadEmployees();
      setJobModalOpen(false);
    } catch (err) {
      console.error("Görev güncellenirken hata oluştu:", err);
      goldToast.error("Görev bilgileri güncellenirken bir hata oluştu.");
    } finally {
      setJobsSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (employees || []).filter(
      (e) =>
        !q ||
        (e.name || "").toLowerCase().includes(q) ||
        (e.surname || "").toLowerCase().includes(q) ||
        (e.id || "").toLowerCase().includes(q)
    );
  }, [employees, search]);

  const stats = useMemo(() => {
    const exited = filtered.filter(isExitedEmployee).length;
    return { total: filtered.length, active: filtered.length - exited, exited };
  }, [filtered]);

  const jobOptions = useMemo(() => jobs.map((j) => ({ value: j.id, label: j.title })), [jobs]);

  const modalRows = useMemo(() => {
    const q = modalSearch.toLowerCase();
    return (employees || []).filter(
      (e) => !q || (e.name || "").toLowerCase().includes(q) || (e.surname || "").toLowerCase().includes(q)
    );
  }, [employees, modalSearch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("tr-TR");
    } catch {
      return dateStr;
    }
  };

  const goToResignation = (id) => navigate(`/istifa?employee=${id}`);

  const columns = [
    { key: "name", dataIndex: "name", title: "Ad", flex: 1, minWidth: 130, render: (v) => <span className="font-semibold text-slate-800">{v}</span> },
    { key: "surname", dataIndex: "surname", title: "Soyad", flex: 1, minWidth: 130, render: (v) => <span className="font-semibold text-slate-800">{v}</span> },
    { key: "hireDate", dataIndex: "hireDate", title: "İşe Giriş Tarihi", type: "date", width: 130, render: (v) => formatDate(v) },
    {
      key: "exitDate",
      dataIndex: "exitDate",
      title: "İşten Çıkış Tarihi",
      width: 140,
      render: (_, e) => formatDate(e.exitDate || e.ExitDate || e.resignationDate || e.ResignationDate),
    },
    {
      key: "gender",
      dataIndex: "gender",
      title: "Cinsiyet",
      width: 100,
      align: "center",
      render: (v) => (
        <Badge size="sm" variant="soft" color="default">
          {v === 1 ? "Erkek" : "Kadın"}
        </Badge>
      ),
    },
    {
      key: "status",
      dataIndex: "id",
      title: "Durum",
      width: 100,
      align: "center",
      render: (_, e) =>
        isExitedEmployee(e) ? (
          <Badge size="sm" radius="full" variant="soft" color="default">
            Ayrıldı
          </Badge>
        ) : (
          <Badge size="sm" radius="full" variant="soft" color="success">
            Aktif
          </Badge>
        ),
    },
    {
      key: "actions",
      dataIndex: "id",
      title: "İşlemler",
      width: 150,
      align: "center",
      render: (id) => (
        <Button size="xs" variant="text" color="primary" title="İstifa Formu Düzenle / İndir" onClick={() => goToResignation(id)}>
          <span className="flex items-center gap-1">
            <FileText size={13} /> İstifa Formu
          </span>
        </Button>
      ),
    },
  ];

  // Grid's own right-click menu, with the resignation shortcut on top.
  const contextMenuItems = (defaults, ctx) =>
    ctx.row
      ? [
          { key: "resignation", label: `📄 İstifa Formu Oluştur / İndir — ${ctx.row.name} ${ctx.row.surname}`, onSelect: () => goToResignation(ctx.row.id) },
          { key: "divider-resignation", divider: true },
          ...defaults,
        ]
      : defaults;

  const jobStateOf = (e) => {
    const originalJobId = resolveOriginalJobId(e, jobs);
    const currentJobId = jobSelections[e.id] !== undefined ? jobSelections[e.id] : originalJobId;
    const modified = String(currentJobId).trim().toLowerCase() !== String(originalJobId).trim().toLowerCase();
    return { currentJobId, modified };
  };

  // Grid cells scroll-clip popups, so the job picker is the native (non-searchable) Select.
  const jobColumns = [
    {
      key: "employee",
      dataIndex: "name",
      title: "Personel Adı Soyadı",
      flex: 1,
      minWidth: 200,
      render: (_, e) => (
        <span className="font-semibold text-slate-800">
          {e.name} {e.surname}
        </span>
      ),
    },
    {
      key: "job",
      dataIndex: "jobId",
      title: "Görev Tanımı",
      width: 300,
      render: (_, e) => (
        <div style={{ width: 280 }}>
          <Select
            size="sm"
            options={[{ value: "", label: "— Görev Seçiniz —" }, ...jobOptions]}
            value={jobStateOf(e).currentJobId}
            disabled={jobsLoading}
            onChange={(ev) => setJobSelections((prev) => ({ ...prev, [e.id]: ev.target.value }))}
          />
        </div>
      ),
    },
    {
      key: "state",
      dataIndex: "id",
      title: "Durum",
      width: 120,
      align: "center",
      render: (_, e) => {
        const { modified } = jobStateOf(e);
        return (
          <Badge size="sm" radius="full" variant="soft" color={modified ? "warning" : "default"}>
            {modified ? "Değiştirildi" : "Güncel"}
          </Badge>
        );
      },
    },
  ];

  return (
    <AppShell pageId="personel">
      <ClassicWindow title="Personel Yönetimi (Çalışan Listesi)" width="100%">
        <Toolbar>
          <div className="flex items-center gap-2 px-1">
            <Calendar size={15} className="text-slate-500" />
            <span className="text-xs font-semibold text-slate-700">Dönem:</span>
            <div className="w-24">
              <Select size="sm" options={YEAR_OPTIONS} value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
            <div className="w-36">
              <Select size="sm" options={MONTH_OPTIONS} value={month} onChange={(e) => setMonth(Number(e.target.value))} />
            </div>
          </div>

          <ToolbarButton
            icon={<RotateCw size={14} className={loading ? "animate-spin" : ""} />}
            label="Yenile"
            onClick={loadEmployees}
            disabled={loading}
          />
          <ToolbarButton
            icon={<Briefcase size={14} />}
            label="Görev Bilgilerini Güncelle"
            onClick={handleOpenJobModal}
            testId="btn-open-job-modal"
          />

          <ToolbarSeparator />

          <div className="w-56">
            <Input
              size="sm"
              icon={<Search size={14} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="personel-search-input"
              placeholder="Personel Ad / Soyad Ara..."
            />
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <Badge size="sm" radius="full" variant="soft" color="default">
              Toplam: {stats.total}
            </Badge>
            <Badge size="sm" radius="full" variant="soft" color="success">
              <span className="flex items-center gap-1">
                <UserCheck size={12} /> Aktif: {stats.active}
              </span>
            </Badge>
            {stats.exited > 0 && (
              <Badge size="sm" radius="full" variant="soft" color="danger">
                <span className="flex items-center gap-1">
                  <UserX size={12} /> Ayrılan: {stats.exited}
                </span>
              </Badge>
            )}
          </div>
        </Toolbar>

        <div className="flex flex-col">
          {error && (
            <div className="p-3">
              <Alert color="danger" variant="filled" size="sm" title="Hata" description={String(error)} />
            </div>
          )}

          <div data-testid="personel-table">
            <FitDataGrid
              uniqueKey="id"
              columns={columns}
              dataSource={filtered}
              loading={loading}
              rowHeight={38}
              contextMenuItems={contextMenuItems}
              emptyFeedback={<Empty size="sm" title="Personel bulunamadı" description="Seçilen döneme ait personel kaydı bulunamadı." />}
            />
          </div>
        </div>
      </ClassicWindow>

      <Modal
        isOpen={jobModalOpen}
        onClose={() => setJobModalOpen(false)}
        width={960}
        className="max-w-[95vw]"
        title={
          <span className="flex items-center gap-2">
            <Briefcase size={16} className="text-amber-500" /> Toplu Görev Bilgilerini Güncelle
          </span>
        }
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <span className="text-[11px] text-slate-500">* Yapılan seçimler "Kaydet" butonuna basıldığında sisteme aktarılır.</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" color="default" onClick={() => setJobModalOpen(false)} disabled={jobsSaving}>
                Vazgeç
              </Button>
              <Button onClick={handleSaveJobs} loading={jobsSaving} disabled={jobsLoading}>
                <span className="flex items-center gap-1.5">
                  <Save size={14} /> Kaydet (Toplu Güncelle)
                </span>
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="w-full max-w-sm">
              <Input
                size="sm"
                icon={<Search size={14} />}
                placeholder="Modal içinde personel ara..."
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
              />
            </div>
            <span className="text-slate-500">
              Tanımlı Görev Sayısı: <b className="text-slate-900">{jobs.length}</b>
            </span>
          </div>
          <div>
            <FitDataGrid
              uniqueKey="id"
              columns={jobColumns}
              dataSource={modalRows}
              loading={jobsLoading}
              rowHeight={44}
              height={Math.round(window.innerHeight * 0.55)}
              showStatusBar={false}
              emptyFeedback={<Empty size="sm" title="Personel bulunamadı" description="Görev bilgisi değiştirilecek personel bulunamadı." />}
            />
          </div>
        </div>
      </Modal>
    </AppShell>
  );
};

export default PersonelListePage;
